import { useState, useEffect } from 'react';
import isEqual from 'lodash.isequal';
import {
  Checkbox,
  InputTime,
  InputSize,
  ButtonSecondary,
  ButtonSize,
  IconClose,
} from '@ui/src';
import {
  Schedule as ScheduleProps,
  WeekDays,
} from '../../hoc/auto-settings/types';
import { ScheduleTypes } from './types';

import cn from './auto-settings.module.css';

const formatWeekdayName = (weekday: WeekDays) => {
  const formattedWeekday = `${weekday.charAt(0)}${weekday
    .slice(1)
    .toLowerCase()}`;

  return formattedWeekday;
};

const Schedule = ({
  schedule,
  schedules,
  setSchedule,
  isEditable,
  withError,
}: ScheduleTypes) => {
  const [scheduleValue, setScheduleValue] = useState<ScheduleProps>();
  const [timerange, setTimerange] = useState<
    { start: string; end: string; id: string }[]
  >(schedule.timerange);

  const onDeleteTimeRange = (id: string) => {
    setTimerange(timerange?.filter((item) => item.id !== id));

    const nextSchedules = schedules?.map((item) => {
      const result = { ...item };
      let nextTimeRange = (
        [] as { id: string; start: string; end: string }[]
      ).concat(result.timerange);

      if (result.weekday === scheduleValue?.weekday) {
        nextTimeRange = nextTimeRange?.filter((range) => range.id !== id);

        result.timerange = nextTimeRange;
      }

      return result;
    });

    setSchedule(nextSchedules);
  };

  const onChangeTime = ({
    name,
    id,
    value,
  }: {
    name: string;
    id: string;
    value: string;
  }) => {
    const nextTimerange = timerange?.map((item) => {
      const result = { ...item };

      if (result.id === id) {
        if (name === 'start') {
          result.start = value;
        }

        if (name === 'end') {
          result.end = value;
        }
      }

      return result;
    });

    const nextSchedules = schedules?.map((item) => {
      let result = { ...item };

      if (result.weekday === scheduleValue?.weekday) {
        result = {
          ...scheduleValue,
          timerange: nextTimerange,
        };
      }

      return result;
    });

    setTimerange(nextTimerange);
    setSchedule(nextSchedules);
  };

  const onChange = (value: boolean) => {
    const nextSchedule = { ...scheduleValue };

    nextSchedule.active = value;

    if (!value) {
      setTimerange([{ start: '', end: '', id: 'range-0' }]);
    }

    if (timerange.length) {
      if (value) {
        nextSchedule.timerange = timerange;
      } else {
        nextSchedule.timerange = [{ start: '', end: '', id: 'range-0' }];
      }
    }

    const nextSchedules = schedules?.map((item) => {
      let result = { ...item };

      if (nextSchedule && result.weekday === nextSchedule?.weekday) {
        result = nextSchedule as ScheduleProps;
      }

      return result;
    });

    setScheduleValue(nextSchedule as ScheduleProps);
    setSchedule(nextSchedules);
  };

  useEffect(() => {
    if ((schedule && !scheduleValue) || !isEqual(schedule, scheduleValue)) {
      setScheduleValue(schedule);
      setTimerange(schedule.timerange);
    }
  }, [schedule, scheduleValue]);

  return (
    <div className={`${cn.schedule} ${scheduleValue?.active ? cn.active : ''}`}>
      <div className={cn.weekday}>
        <Checkbox
          type="checkbox"
          name="active"
          checked={scheduleValue?.active}
          title={formatWeekdayName(schedule.weekday)}
          onChange={(e) => onChange(e.target.checked)}
          disabled={!isEditable}
        />
      </div>
      {scheduleValue && scheduleValue.active && (
        <div className={cn.timerangeWrapper}>
          {timerange.map((item, index) => (
            <div className={cn.timerange} key={item.id}>
              <InputTime
                name="start"
                value={item.start}
                size={InputSize.SMALL}
                placeholder="00:00"
                labelClassName={cn.timeInput}
                onChange={(e) =>
                  onChangeTime({
                    name: 'start',
                    id: item.id,
                    value: e.target.value,
                  })
                }
                disabled={!isEditable}
              />
              <div className={cn.separatorWrapper}>
                <div className={cn.separator} />
              </div>
              <InputTime
                name="end"
                value={item.end}
                size={InputSize.SMALL}
                placeholder="00:00"
                labelClassName={`${cn.timeInput} ${
                  index !== 0 ? cn.smallWidth : ''
                }`}
                onChange={(e) =>
                  onChangeTime({
                    name: 'end',
                    id: item.id,
                    value: e.target.value,
                  })
                }
                disabled={!isEditable}
              />
              {index !== 0 && isEditable && (
                <button
                  type="button"
                  className={cn.removeTimerange}
                  aria-label="Remove time range"
                  onClick={() => onDeleteTimeRange(item.id)}
                >
                  <IconClose />
                </button>
              )}
            </div>
          ))}
          {withError && (
            <div className={cn.weekdayError}>
              The day of the week must contain at least 1 time period
            </div>
          )}
          {isEditable && (
            <ButtonSecondary
              className={`button-white ${cn.addRangeButton}`}
              size={ButtonSize.SMALL}
              text="Add time range"
              onClick={() => {
                const nextTimerange = (
                  [] as { start: string; end: string; id: string }[]
                ).concat(timerange);

                nextTimerange.push({
                  id: `range-${timerange.length + 1}`,
                  start: '',
                  end: '',
                });

                setTimerange(nextTimerange);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Schedule;
